// Import Swiper styles
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { Virtual } from 'swiper/modules'
import { useCallback, useEffect, useRef } from 'react'
import { StoryPeerType } from '@weblite/ts-action/story'
import { PeerStoryViewer } from '$comp/_app/components/story/peer-story-viewer/peer-story-viewer'
import {
  useStoryRibbons,
  useSelectedRibbonStory,
} from '$logic/story/react-hooks'

export function MobileSlider({
  peerId,
  peerType,
  isOpen,
  onSwipeNext,
  onSwipePrevious,
  onClose,
  onCreateStory,
}: {
  peerId: string
  peerType: StoryPeerType
  isOpen: boolean
  onSwipeNext: () => void
  onSwipePrevious: () => void
  onClose: () => Promise<void>
  onCreateStory: (peerId: string, peerType: StoryPeerType) => void
}) {
  const { allRibbons } = useStoryRibbons()

  const enteringSwipeHappenedRef = useRef(false)

  const isSliderMovingRef = useRef(false)
  const selectedRibbonStoryIndex = useSelectedRibbonStory(peerId, peerType)
  useEffect(() => {
    if (selectedRibbonStoryIndex === 0) enteringSwipeHappenedRef.current = true
  }, [selectedRibbonStoryIndex])

  const swiperRef = useRef<SwiperClass>()
  const handleInitSwiper = useCallback((swiper: SwiperClass) => {
    swiperRef.current = swiper
  }, [])

  const handleSliderMoveStart = useCallback(() => {
    isSliderMovingRef.current = true
  }, [])

  const handleSliderMoveEnd = useCallback(() => {
    isSliderMovingRef.current = false
  }, [])

  const handleGoToNextSlide = useCallback(() => {
    const swiper = swiperRef.current
    if (!swiper) return
    if (swiper.isEnd) onClose()
    else swiper.slideNext()
  }, [])

  const handleGoToPrevSlide = useCallback(() => {
    const swiper = swiperRef.current
    if (!swiper) return
    if (swiper.isBeginning) onClose()
    else swiper.slidePrev()
  }, [])

  const handleSwipeNext = useCallback(() => {
    if (enteringSwipeHappenedRef.current) {
      onSwipeNext()
    } else {
      enteringSwipeHappenedRef.current = true
    }
  }, [onSwipeNext])

  useEffect(() => {
    if (!allRibbons.length) {
      onClose()
    }
  }, [allRibbons])

  return (
    <Swiper
      style={{ width: '100%', height: '100%', maxWidth: '400px' }}
      modules={[Virtual]}
      virtual
      initialSlide={selectedRibbonStoryIndex}
      dir="ltr"
      touchStartPreventDefault={false}
      onTransitionStart={handleSliderMoveEnd}
      onSlideNextTransitionEnd={handleSwipeNext}
      onSlidePrevTransitionEnd={onSwipePrevious}
      onSliderMove={handleSliderMoveStart}
      onSwiper={handleInitSwiper}
    >
      {allRibbons.map((storyRibbon, index) => (
        <SwiperSlide
          dir="rtl"
          virtualIndex={index}
          key={storyRibbon.peerId + storyRibbon.peerType}
        >
          {({ isActive }) => (
            <PeerStoryViewer
              onCreateStory={onCreateStory}
              onClose={onClose}
              isActive={isActive}
              storyRibbon={storyRibbon}
              isSliderMovingRef={isSliderMovingRef}
              goToNextSlide={handleGoToNextSlide}
              goToPrevSlide={handleGoToPrevSlide}
              isModalOpen={isOpen}
              ribbonsCount={allRibbons.length}
            />
          )}
        </SwiperSlide>
      ))}
    </Swiper>
  )
}
